import { useCallback, useEffect, useState, useMemo } from 'react'
import log from 'loglevel'
import useRawPromise from './useRawPromise'

// GET resources as react state. For other methods, use the useRawPromise promises

const defaultPages = {
    first: undefined,
    last: undefined,
    next: undefined,
}

/**
 * @returns {{
 *  data: any,
 *  error: any,
 *  fetch: () => void,
 *  isBusy: boolean,
 *  firstPage: undefined | number,
 *  lastPage: undefined | number,
 *  nextPage: undefined | number,
 * }}
 */

export default function useRawState(
    resource,
    query = {},
    dependencies = []
) {
    if (!Array.isArray(dependencies)) {
        throw new Error('received dependencies were not an array')
    }

    const { get } = useRawPromise()

    const [data, setData] = useState(undefined)
    const [pages, setPages] = useState(defaultPages) // for paginated responses
    const [error, setError] = useState(undefined)
    const [isBusy, setIsBusy] = useState(true)

    const queryStr = useMemo(() => {
        // to stabilise the dependencies of the fetch func
        return Object.entries(query).map(entry => `${entry[0]}${entry[1]}`).join('-')
    }, [query])

    const fetch = useCallback(async () => {
        if (resource === undefined) {
            setData(undefined)
            setPages(defaultPages)
            setIsBusy(false)
            setError(undefined)
            return
        }

        try {
            setIsBusy(true)
            setData(undefined)

            const { data: resData, error } = await get(resource, {
                query,
                unpackHydra: false
            })

            setError(error)

            if (resData["hydra:member"]) { // handle paginated response
                setData(resData["hydra:member"])

                if (resData["hydra:member"].length === resData["hydra:totalItems"]) {
                    setPages(defaultPages)
                } else {
                    setPages({
                        first: resData["hydra:view"]["hydra:first"],
                        last: resData["hydra:view"]["hydra:last"],
                        next: resData["hydra:view"]["hydra:next"],
                    })
                }
            } else {
                setData(resData)
                setPages(defaultPages)
            }

        } catch (err) {
            log.error(err)
            setError(err)
            setData(undefined)
            setPages(defaultPages)
        } finally {
            setIsBusy(false)
        }
    }, [resource, get, queryStr])

    useEffect(() => {
        if (!dependencies.includes(undefined)) {
            fetch()
        }
        else {
            setIsBusy(false)
            setData(undefined)
            setPages(defaultPages)
            setError(undefined)
        }
    }, [fetch, ...dependencies])

    return {
        data,
        error,
        fetch,
        isBusy,
        firstPage: pages.first,
        lastPage: pages.last,
        nextPage: pages.next,
    }
}